import React, { useEffect, useRef, useState } from "react"

import { RouteComponentProps } from "react-router-dom"
import { gql, useClient, useQuery } from "urql"
import { pipe, subscribe } from "wonka"

import { AlertLevel, ValueFormat } from "~tailwindui/types/enums"

import { AlertMessageBox, WithLoadingIndicator } from "~tailwindui/Basics"
import ChipDetails from "~tailwindui/ChipDetails"

import createBlob from "src/createBlob"
import { surveyOptionsWithIcons } from "src/enums"
import titlecase from "src/titlecase"
import { MatchParams } from "src/types/MatchParams"

import SubHeader from "components/OrganizationAdmin/components/DisplayableFormikForm/SubHeader"
import DonutChart from "components/OrganizationAdmin/components/DonutChart"

const SurveyDetails: React.FC<RouteComponentProps<MatchParams>> = ({
  match: {
    params: { id },
  },
}) => {
  const client = useClient()
  const [csvUrl, setCsvUrl] = useState<string>()
  const [exportMessages, setExportMessages] = useState<string[]>([])
  const [messageAlertLevel, setMessageAlertLevel] =
    useState<AlertLevel | null>()
  const csvDownloadLink = useRef<HTMLAnchorElement>()
  const [result] = useQuery({ query: surveyQuery, variables: { id } })

  const requestExport = () => {
    const { unsubscribe } = pipe(
      client.subscription(surveyResultsSubscription, { id }),
      subscribe((result: any) => {
        if (result.error) {
          const errors: string[] = []
          if (
            process.env.NODE_ENV === "development" &&
            result.error?.networkError?.error
          ) {
            setMessageAlertLevel(AlertLevel.Error)
            errors.push(result.error.networkError.error)
          }
          setExportMessages(errors.concat([result.error.networkError.error]))
          return
        }

        const { status, errorMessages, csv } = result.data.surveyResults

        if (errorMessages.length) {
          setExportMessages(errorMessages)
          unsubscribe()
        } else {
          if (status === "done") {
            const blobUrl = createBlob(csv, "text/csv")
            setCsvUrl(blobUrl)
            setMessageAlertLevel(AlertLevel.Info)
            setExportMessages([
              "Export completed. Please check your downloads folder.",
            ])
            unsubscribe()
          } else if (status === "enqueued") {
            setMessageAlertLevel(AlertLevel.Info)
            setExportMessages([
              "Export enqueued. Please bear with us as this process can take a while. Leave this window open and your file will be downloaded once it's ready.",
            ])
          } else {
            setMessageAlertLevel(AlertLevel.Info)
            setExportMessages([titlecase(status)])
          }
        }
      })
    )
  }

  const buildChartData = question => [
    {
      name: "Strongly Disagree",
      value: question.results.find(result => result.value === 0)?.count,
    },
    {
      name: "Disagree",
      value: question.results.find(result => result.value === 1)?.count,
    },
    {
      name: "Neither Agree Nor Disagree",
      value: question.results.find(result => result.value === 2)?.count,
    },
    {
      name: "Agree",
      value: question.results.find(result => result.value === 3)?.count,
    },
    {
      name: "Strongly Agree",
      value: question.results.find(result => result.value === 4)?.count,
    },
  ]

  useEffect(() => {
    if (!csvUrl) return
    csvDownloadLink.current.click()
    setCsvUrl(null)
    setTimeout(() => URL.revokeObjectURL(csvUrl), 0)
  }, [csvUrl])

  return (
    <WithLoadingIndicator result={result}>
      {({ data: { survey } }) =>
        survey === null ? (
          <AlertMessageBox>
            You do not have access to this survey.
          </AlertMessageBox>
        ) : (
          <>
            <a
              ref={csvDownloadLink}
              href={csvUrl}
              download="Survey Results.csv"
              className="hidden"
            >
              csv
            </a>
            <ChipDetails
              details={survey.questions.map(question => ({
                label: question.text,
                stats: surveyOptionsWithIcons.map(answer => {
                  const result = question.results.find(
                    result => result.value === answer.value
                  )
                  return {
                    key: answer.value,
                    label: answer.label,
                    value: result?.percent,
                    valueFormat: ValueFormat.Percent,
                  }
                }),
              }))}
            />

            <div className="mt-5">
              <button
                type="button"
                onClick={requestExport}
                className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                Export Results
              </button>

              {exportMessages.length > 0 && (
                <div className="mt-3 md:w-2/3 lg:w-1/2">
                  <AlertMessageBox level={messageAlertLevel}>
                    <div className="flex list-none flex-col space-y-1 pl-5">
                      {exportMessages.map(message => (
                        <li key={message}>{message}</li>
                      ))}
                    </div>
                  </AlertMessageBox>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2">
              {survey.questions.map(question => (
                <div key={question.id}>
                  <SubHeader title={question.text} />
                  <DonutChart
                    color={[
                      "#DC2626",
                      "#F97316",
                      "#EAB308",
                      "#84CC16",
                      "#16A34A",
                    ]}
                    title=""
                    data={buildChartData(question)}
                    height="medium"
                  />
                </div>
              ))}
            </div>
          </>
        )
      }
    </WithLoadingIndicator>
  )
}

const surveyQuery = gql`
  query Survey($id: ID!) {
    survey(id: $id) {
      id
      name
      startsOn
      questions {
        id
        text
        results(surveyId: $id) {
          value
          count
          percent
        }
      }
    }
  }
`

const surveyResultsSubscription = gql`
  subscription RequestSurveyResults($id: ID!) {
    surveyResults(surveyId: $id) {
      status
      csv
      errorMessages
    }
  }
`

export default SurveyDetails
